import type { TemplateOption } from 'candidate_profile_redesign/shared/types';
import type { Item } from '@grnhse/seedling/lib/birch/components/forms/dropdown/item';
import { SCORECARD_STATUS } from 'talent_search/shared/types';
import type AdvancedFilter from 'talent_matching/components/filters/advanced_filter';
import { FraudReport } from 'fraud/types';
export type Question = {
  question: string;
  question_label: string;
  question_description?: string;
  answer: string;
  hidden?: boolean;
  value_type?: string;
};

export interface Geolocation {
  latitude: string;
  longitude: string;
  location: string;
  label: string;
  value: string;
}

export type CompanyMode = 'include' | 'exclude';
export type CompanyNameFilter = {
  id: string;
  mode: CompanyMode;
  value: string;
};

export type MatchStrength =
  | 'unknown'
  | 'limited_match'
  | 'partial_match'
  | 'good_match'
  | 'strong_match';

export type MatchStrengthExplanationType = {
  experience_explanation: string | null;
  skills_explanation: string | null;
  industry_explanation: string | null;
  unknown: boolean;
  no_matched_criteria: boolean;
};

export type Scorecard = {
  author: string;
  body: string;
  candidate_rating_id: number;
  created_at: string;
  decision: { key: string; label: string };
  id: number;
  is_current_user?: boolean;
};

export type OverrideScore = {
  match_strength: MatchStrength;
  note: string;
  reason: string;
};

export type ResumeEmployment = {
  title: string;
  employer: string | null;
  start_date: string | null;
  end_date: string | null;
};

type ResumeDetails = {
  address: string | null;
  employments: ResumeEmployment[];
};

export type Document = {
  id?: number;
  downloadUrl: string;
  extension: string;
  display_name?: string;
  file_name: string;
  type: string;
  virus_status?: string;
  visibility?: string;
  previewUrl?: string; // standard resume
  preview_path?: string | null; // anonymized resume
  previewable?: boolean;
};

export type SidePanelDetails = {
  personId: number;
  advancePath: string | null;
  hasOtherApplications: boolean;
  applicationHistoryPath: string;
  applicationQuestions: Question[];
  agencyQuestions: Question[];
  referralDetails: Question[];
  lastSeenFraudReport: string | null;
  attachments?: Document[];
  paths: {
    documentsPath: string;
    fraudReportPath: string;
    updateLastSeenFraudReportPath: string;
    restoreFraudReportPath?: string;
    resolveFraudReportPath?: string;
  };
};

export type Row = {
  id: number;
};

export type ResumeParsingStatus = 'in_progress' | 'complete' | 'failed';

export type BaseResume = {
  url: string;
  type: 'docx' | 'pdf' | 'txt' | 'rtf';
  document: Document;
};

export type BaseCandidate = {
  id: number;
  fullName: string;
  tags: Array<{ label: string; value: string | number }>;
  linkedinAddress: string | null;
  githubAddress?: string | null;
  websiteAddress?: string | null;
  referral: boolean;
  internal: boolean;
  activeOnAnotherJob?: boolean;
  potentialDuplicate?: boolean;
  resume?: BaseResume | null;
};

export type MatchedSkill = {
  name: string;
  importance: number;
  exact: boolean;
  base_skill: string;
};

export type ApplicationScore = {
  matchScore: number;
  matchedSkills: MatchedSkill[];
  numCalibrationSkills: number;
  matchStrength: MatchStrength | null;
  matchStrengthExplanation: MatchStrengthExplanationType;
  overrideMatchScorePath?: string;
  overrideMatchStrength?: MatchStrength | null;
  overrideNote?: string | null;
  overrideReason?: string | null;
};

export type PersonTags = {
  tags: Array<{ label: string; value: number }>;
};

export type Person = BaseCandidate & {
  appliedOn?: string;
  sourceName?: string | null;
  company: string;
  title: string;
  profileLocation: string | null;
  latestEmployment: {
    company_name: string;
    title: string;
    start_date: string;
    end_date: string | null;
  } | null;
  hiringPlanId?: number;
  currentHiringPlanInterviewStage: { name: string; id: number } | null;
  nextHiringPlanInterviewStage: { name: string; id: number } | null;
  linkedinIframePath: string | null;
  resume?:
    | (BaseResume & {
        details: ResumeDetails;
        parsing_status?: ResumeParsingStatus;
      })
    | null;
  fromAgency: boolean;
  previouslyRejectedFromJob: boolean;
  dreamJob: boolean;
  submitFeedbackPath: string;
  jobPost: { title: string } | null;
  rejectPath: string;
  rejectModalPath: string;
  addTagsPath: string;
  tags: Item[];
  isBookmarked: boolean;
  scorecards?: Scorecard[];
  userScorecard?: Scorecard;
  emailCandidatePath: string;
  candidateProfileContextPath: string;
  transferModalHiringPlansPath: string;
  transferModalOwnerOptionsPath: string;
  email: string | null;
  addPersonToJobPath: string;
  fraudReportHighRisk: boolean;
  fraudReport?: FraudReport;
  decision?: { status: string };
} & Partial<SidePanelDetails> &
  Partial<ApplicationScore>;

export type RediscoveryAction = {
  id: number;
  action: 'added_as_prospect' | 'tags_amended' | 'sent_email';
  metadata: Record<string, unknown> | null;
  organization_id: number;
  hiring_plan_id: number;
  application_id: number;
  user_id: number;
  created_at: string;
  updated_at: string;
};

export type RediscoveryApplication = BaseCandidate & {
  appliedOn: string;
  hiringPlanId: number;
  hiringPlanName: string;
  currentStage: { id: number; name: string } | null;
  rejectedOn: string | null;
  rejectionReason: string | null;
  resume?: (BaseResume & { parsing_status: string }) | null;
  candidateProfilePath: string;
  activeAppsCount: number;
  pastAppsCount: number;
  milestone: string | null;
  positiveRatingPercentage: number | null;
  rediscoveryActions: RediscoveryAction[];
  addTagsPath: string;
  makeProspectPath: string;
  sourceAndResponsibilityPath: string;
  candidateProfileContextPath: string;
  emailCandidatePath: string;
};

export type TalentMatchingApplication = Partial<Person> & {
  id: number;
  hiringPlanId: number;
  fullName: string;
};

export type ScorecardRatingsSummary = {
  strong_no: number;
  no: number;
  yes: number;
  strong_yes: number;
  no_decision: number;
};

export type ScorecardDetail = {
  id: number;
  interviewer_name: string | null;
  interview_name: string | null;
  rating: string;
  scorecard_url: string;
};

export type RediscoveryApplicationHistoryItem = {
  id: number;
  hiring_plan_name: string;
  url: string;
  hiring_plan_url: string;
  requisition_id: string | null;
  recruiter: string | null;
  application_stage_name: string | null;
  days_in_stage: number | null;
  applied_date: string;
  source: string | null;
  rejected_on: string | null;
  hired_on: string | null;
  scorecard_ratings_summary: ScorecardRatingsSummary;
  scorecard_details: ScorecardDetail[];
  attachments: Document[];
  matching_application?: boolean;
};

export type AiScorecardSummaryInfo = {
  overall_sentiment: string | null;
  stale: boolean;
  task_id: number | null;
  start_summary_path: string;
  check_summary_status_path: string;
};

export type RediscoveryCurrentApplication =
  RediscoveryApplicationHistoryItem & {
    milestone: string | null;
    rejection_reason: string | null;
    positive_rating_percentage: number | null;
    ai_scorecard_summary: AiScorecardSummaryInfo | null;
  };

export type RediscoveryApplicationHistory = {
  currentApplication: RediscoveryCurrentApplication;
  allApplicationsCount: number;
  activeApplications: RediscoveryApplicationHistoryItem[];
  previousApplications: RediscoveryApplicationHistoryItem[];
  candidateProfileUrl: string;
};

// eslint-disable-next-line no-shadow
export enum AppliedOnType {
  YESTERDAY = 'yesterday',
  TODAY = 'today',
  LAST_SEVEN_DAYS = 'last_seven_days',
  LAST_6_MONTHS = 'last_6_months',
  LAST_12_MONTHS = 'last_12_months',
  LAST_2_YEARS = 'last_2_years',
  BETWEEN = 'between',
  ON_OR_AFTER = 'on_or_after',
  ON_OR_BEFORE = 'on_or_before',
}

export interface FilterType extends Item<string> {
  operators: Item[];
  valueType:
    | 'short_text'
    | 'long_text'
    | 'yes_no'
    | 'single_select'
    | 'multi_select'
    | 'currency'
    | 'number'
    | 'date'
    | 'user';
  valuesPath?: string;
  values?: Item[];
}

export type AdvancedFilterType = {
  type: FilterType | null;
  operator: Item | null;
  value: Item | Item[] | string | null;
  reactKey?: string;
};

export interface TalentMatchingFilters {
  page: number;
  perPage: number;
  appliedOnEnd: string | null;
  appliedOnStart: string | null;
  appliedOnType: AppliedOnType | null;
  jobPosts: Item[];
  degrees: Array<string>;
  disciplines: Array<string>;
  excludedIds: number[];
  feedbackStatus: SCORECARD_STATUS;
  hideRejected: boolean;
  jobAlertSubscribers: boolean;
  locationFilterType: 'name' | 'radius';
  locationName: string | null;
  debouncedLocationName: string | null;
  geolocation: Geolocation | null;
  geolocationRadius: string;
  manualReview: boolean;
  priorityCandidates: boolean;
  savedForLater: boolean;
  schools: Array<string>;
  tags: Array<string>;
  canHave: Array<string>;
  mustHave: Array<string>;
  doesNotHave: Array<string>;
  excludePreviouslyRejected: boolean;
  excludeHighRisk: boolean;
  otherActiveApplications: '1' | '0' | null;
  minExperience: string | null;
  maxExperience: string | null;
  companyNameFilters: CompanyNameFilter[];
  educationFilters: AdvancedFilter[];
  advancedFilters: AdvancedFilter[];
  questionFilters: AdvancedFilter[];
}

export interface CustomFieldParams {
  [key: `cf_${string}`]: Array<string>;
  [key: `txt_cf_${string}`]: Array<string>;
  [key: `defined_cf_${string}`]: '1' | '0';
}

interface UnlinkedQuestionParams {
  [key: `unlinked_q_${string}`]: Array<string>;
  [key: `txt_unlinked_q_${string}`]: Array<string>;
  [key: `defined_unlinked_q_${string}`]: '1' | '0';
}

export type AdvancedFilterParams = CustomFieldParams &
  UnlinkedQuestionParams & {
    any_candidate_tag_id?: string[];
    candidate_tag_id?: string[];
    has_cover_letter?: string[];
    source_id?: string[];
    without_source_id?: string[];
    application_question?: string[];
    agency_question?: string[];
    referral_question?: string[];
    degree_contains?: string[];
    degree_id?: string[];
    discipline_contains?: string[];
    discipline_id?: string[];
    school_name_contains?: string[];
    school_name_id?: string[];
  };

interface FilterParams {
  application_created_at: string | undefined | null;
  application_created_end: string | undefined | null;
  job_post_id?: string[];
  exclude_application_ids: number[];
  exclude_previously_rejected: boolean;
  geolocation?: Geolocation;
  geolocation_radius?: string;
  manual_review: boolean;
  priority_candidates: boolean;
  without_potential_fraud: boolean;
  saved_for_later: boolean;
  other_active_applications?: '1' | '0' | null;
  scorecard_status: SCORECARD_STATUS;
  years_of_experience_min: string | null;
  years_of_experience_max: string | null;
  can_have?: string[];
  must_have?: string[];
  does_not_have?: string[];
}

export interface TalentMatchingQueryParams extends AdvancedFilterParams {
  queryParams: {
    page: number;
    page_size: number;
  };
  filterParams: FilterParams;
}

export type TalentMatchingFilterValue =
  TalentMatchingFilters[keyof TalentMatchingFilters];

// Type that extracts keys of TalentMatchingFilters where the value is boolean
export type BooleanFilterKey = {
  [K in keyof TalentMatchingFilters]: TalentMatchingFilters[K] extends boolean
    ? K
    : never;
}[keyof TalentMatchingFilters];

export interface Paths {
  allJobsPath: string;
  jobDashboardPath: string;
  bulkRejectionModalPath: string;
  advanceStagePath: string | null;
  topSkillsPath: string;
  calibrationSettingsPath: string;
  calibrationHistoryPath: string;
  peoplePath: string;
  referralQuestionOptionsPath: string;
  applicationQuestionOptionsPath: string;
  agencyQuestionOptionsPath: string;
  emailModalPath: string;
  bulkAddTagsPath: string;
  bulkFeedbackPath: string;
  bulkRequestFraudReportsPath: string;
  hiringPlanTagsPath: string;
  exportScoresPath: string;
  mapboxSuggestionsPath: string;
  rankingInProgressPath: string;
  indexingStatusPath: string;
  jobPostOptionsPath: string;
  generateSkillsDescriptionPath: string;
  generateCalibrationFromTextPath: string;
  appReviewPath: string;
  savedSearchesPath: string;
  saveAllCalibrationsPath: string;
  saveAndEmailScoresPath: string;
  industriesPath: string;
}

export type Skill = {
  name: string;
  importance: number;
  newManuallyAdded?: boolean;
  isPipelineSkill?: boolean;
};

export type SkillValidationError = {
  skill: string;
  category:
    | 'subjective_unmeasurable'
    | 'explicit_protected'
    | 'implicit_bias'
    | 'unrecognized'
    | 'errors';
  severity: 'warn' | 'block';
  reason: string;
};

export interface CalibrationSettings {
  skills: Skill[];
  job_titles: string[];
  use_boolean_search: boolean;
  min_years_experience?: number | '';
  dev_only_all_apps?: boolean;
  proceed_despite_warnings?: boolean;
  warned_skill_names?: string[];
  prioritize_current_experience?: boolean;
  extracted_skills?: string[];
  industries?: string[];
  userPrompt?: string;
  include_job_description?: boolean;
  include_scorecard_attributes?: boolean;
  suggested_prompt?: string | null;
  updated_by_id?: number | null;
  updated_by_name?: string | null;
}

export type SettingsFormData = {
  skills: Skill[];
  job_titles: string[];
  use_boolean_search: boolean;
  min_years_experience?: number | '';
  dev_only_all_apps?: boolean;
  prioritize_current_experience?: boolean;
  include_job_description?: boolean;
  include_scorecard_attributes?: boolean;
  userPrompt?: string;
  jobPostSkills?: Skill[];
  scorecardSkills?: Skill[];
  pipelineSkills?: Skill[];
  userPromptMode?: 'initial' | 'view' | 'edit';
  extracted_skills?: string[];
  industries?: string[];
};

export type CalibrationHistoryEntry = CalibrationSettings & {
  id: number;
  created_at?: string;
  updated_at?: string;
  has_criteria?: boolean;
  formatted_created_at?: string | null;
  is_active?: boolean;
};

export type CalibrationHistoryResponse = {
  history: CalibrationHistoryEntry[];
};

export type CalibrationSettingsResponse = {
  settings: CalibrationSettings | null;
  has_job_description: boolean;
  has_scorecard_attributes: boolean;
  suggested_prompt?: string | null;
};

export type SendEmailModalOptionsResponse = {
  defaultTemplate: TemplateOption | null;
  templateOptions: TemplateOption[];
  fromOptions: Item[];
  getCcOptionsPath: string;
  bulkEmailCandidatesPath: string;
};

export type AiFeedbackMap = {
  [key: string]: {
    negative_feedback: Array<string>;
    positive_feedback: Array<string>;
  };
};

export type FilterGroup = Item & {
  items: FilterType[];
};

export type QuestionFilterConfig = {
  filterGroups: FilterGroup[];
};

export type EducationFilterConfig = {
  filterGroups: FilterGroup[];
};

export type AdvancedFilterConfig = {
  filterGroups: FilterGroup[];
  currencies: Item[];
};

export type RankingDisabledReason = {
  text: string;
  link_text: string;
  path: string;
};

export type OverrideMatchScoreParams = {
  application_id: number;
  match_strength: number;
  note: string;
  reason: string;
};

export type OverrideMatchScoreResponse = {
  match_strength_override: MatchStrength | null;
  override_note: string;
  override_reason: string;
};

export type OverrideMatchScoreFormData = {
  match_strength: Item | null;
  note: string;
  reason: string | null;
};

export type { Props as RediscoveryProps } from 'talent_search/talent_rediscovery/container';

export type MyGreenhousePaths = {
  candidatesPath: string;
  savedSearchesPath: string;
  locationAutocompletePath: string;
};

export type MyGreenhouseProps = {
  paths: MyGreenhousePaths;
  savedSearchOptions: Array<{ label: string; value: string }>;
};
